import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { ConversaoLibraConverte } from '../../../shared/conversao-libra/conversao-libra-converte';
import { ExportExcelService } from '../../../shared/excel-service/export-excel.service';
import { FiltroDealers } from './filtro-dealer';
import { VeiculoRegionalFiltroService } from './veiculo-regional-filtro.service';
import { VeiculoRegionalService } from './veiculo-regional.service';

@Component({
  selector: 'app-veiculos-regionais',
  templateUrl: './veiculos-regionais.component.html',
  styleUrls: ['./veiculos-regionais.component.scss']
})
export class VeiculosRegionaisComponent implements OnInit {

  // Propriedades de controle
  tipoRelatorio: 'regional' | 'faturamentoDiario' = 'regional';
  validaButtonRedireciona: boolean = false;
  loading: boolean = false;

  // Form e dados
  form: FormGroup;
  dadosGrid: any = null; // Pode ser array (regional) ou objeto (faturamento diário)
  filtrosDealers: FiltroDealers;
  regioes: any[] = [];

  // Propriedades de filtro
  dataInicial: Date;
  dataFinal: Date;
  maxDate: Date = new Date();
  minDateFinal: Date;
  maxDateFinal: Date;

  // Propriedades para controle de avisos de mudança de data
  dataInicialAnterior: Date;
  dataFinalAnterior: Date;
  mostrarAvisoMudancaData: boolean = false;
  dadosDesatualizados: boolean = false;
  
  // Propriedades para controlar quando as datas foram aplicadas ao filtro
  dataInicialAplicada: Date;
  dataFinalAplicada: Date;

  // Displays
  displayFiltros: boolean = false;
  displayFiltrosDealers: boolean = false;

  // Subscription
  subscription: any;
  filtros: any = {};

  constructor(
    private route: ActivatedRoute,
    private translate: TranslateService,
    private service: VeiculoRegionalService,
    private servico: VeiculoRegionalFiltroService,
    private exportExcelService: ExportExcelService,
    private conversaoLibraConverte: ConversaoLibraConverte,
    private fb: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private date: DatePipe
  ) {
    this.validaButtonRedireciona = !this.route.snapshot.queryParams.fabricaId;
    this.filtrosDealers = new FiltroDealers(this.servico);
    this.tratarData();

    this.form = this.fb.group({
      data_inicial: [moment(this.dataInicial).format('DD-MM-YYYY')],
      data_final: [moment(this.dataFinal).format('DD-MM-YYYY')],
      dealers: [''],
      regiao: [''],
    });

    this.maxDate.setDate(this.maxDate.getDate());
  }

  ngOnInit(): void {
    const now = new Date();
    this.dataFinal = new Date();
    this.dataInicial = new Date(now.getFullYear(), now.getMonth(), 1);

    // Atualizar o formulário com as datas iniciais
    this.f.data_inicial.setValue(moment(this.dataInicial).format('DD-MM-YYYY'));
    this.f.data_final.setValue(moment(this.dataFinal).format('DD-MM-YYYY'));

    this.route.queryParams.subscribe(params => {
      const temParametros = (params.chassi && params.chassi !== '') || (params.cpf && params.cpf !== '');

      if (temParametros) {
        const vdataInicial = params.dataInicial;
        const vdataFinal = params.dataFinal;

        if (moment(vdataInicial, 'DD/MM/YYYY', true).isValid()) {
          this.dataInicial = moment(vdataInicial, 'DD/MM/YYYY').toDate();
        } else {
          console.error('Data inicial inválida:', vdataInicial);
          this.dataInicial = new Date();
        }

        if (moment(vdataFinal, 'DD/MM/YYYY', true).isValid()) {
          this.dataFinal = moment(vdataFinal, 'DD/MM/YYYY').toDate();
        } else {
          console.error('Data final inválida:', vdataFinal);
          this.dataFinal = new Date();
        }

        this.f.data_inicial.setValue(moment(this.dataInicial).format('DD/MM/YYYY'));
        this.f.data_final.setValue(moment(this.dataFinal).format('DD/MM/YYYY'));

        this.getAll();
        this.validaButtonRedireciona = true;
      } else {
        this.validaButtonRedireciona = false;
      }
    });

    this.changeLanguageValue();
    this.preencherRegioes();
    this.filtrosDealers.carregar();
    this.onDataInicialChange();
    this.getAll();
  }

  get f() { return this.form.controls; }

  public alterarTipoRelatorio(tipo: 'regional' | 'faturamentoDiario') {
    this.tipoRelatorio = tipo;
    
    // Limpar dados baseado no novo tipo de relatório
    if (this.tipoRelatorio === 'faturamentoDiario') {
      this.dadosGrid = null;
    } else {
      this.dadosGrid = [];
    }
    
    this.getAll(); // Recarregar dados quando muda o tipo
  }

  public get isRelatorioRegional(): boolean {
    return this.tipoRelatorio === 'regional';
  }

  public get isFaturamentoDiario(): boolean {
    return this.tipoRelatorio === 'faturamentoDiario';
  }

  private tratarData() {
    const dataInicio = new Date();
    const dataFim = new Date();
    let firstDay = new Date(dataInicio.getFullYear(), dataInicio.getMonth(), 1);
    this.dataInicial = firstDay;
    this.dataFinal = dataFim;
  }

  private preencherRegioes() {
    const promise = this.servico.getFiltroRegional();

    promise.then(r => { 
      this.regioes = (r.filtros || []).map(filtro => ({
        nome: filtro.descricao,
        valor: filtro.id.toString()
      }));
      
      this.regioes.unshift({ 
        nome: this.translate.instant('relatorioVeiculos.veiculoRegional.filtros.todosRegionais'), 
        valor: '' 
      });
    }).catch(error => {
      console.error('Erro ao buscar regiões:', error);
      this.regioes = [];
    });
  }

  private getAll() {
    // Verificar se as datas estão definidas
    if (!this.f.data_inicial.value || !this.f.data_final.value) {
      console.error('Datas não estão definidas no formulário');
      this.loading = false;
      return;
    }
    
    this.loading = true;
    
    this.service.getDados(
      this.f.data_inicial.value, 
      this.f.data_final.value, 
      this.f.dealers.value, 
      this.f.regiao.value, 
      this.tipoRelatorio
    ).subscribe(dados => {
        // Verificar se é faturamento diário (objeto) ou regional (array)
        if (this.tipoRelatorio === 'faturamentoDiario') {
          // Para faturamento diário, dados é um objeto { dataInicial, dataFinal, dados: [...] }
          if (dados && dados.dados && Array.isArray(dados.dados)) {
            this.dadosGrid = dados; // Passar o objeto completo
          } else {
            this.dadosGrid = null;
          }
        } else {
          // Para regional, dados é um array
          if (dados && Array.isArray(dados) && dados.length > 0) {
            this.dadosGrid = dados;
          } else {
            this.dadosGrid = [];
          }
        }
        
        // Armazenar as datas atuais como anteriores após carregar os dados
        this.dataInicialAnterior = new Date(this.dataInicial);
        this.dataFinalAnterior = new Date(this.dataFinal);
        
            // Definir as datas aplicadas quando os dados são carregados
    if (!this.dataInicialAplicada || !this.dataFinalAplicada) {
      this.dataInicialAplicada = new Date(this.dataInicial);
      this.dataFinalAplicada = new Date(this.dataFinal);
    }
    

        
        this.mostrarAvisoMudancaData = false;
        this.dadosDesatualizados = false;
        
        this.loading = false;
      }, error => {
        console.error('Erro ao buscar dados:', error);
        // Limpar dados baseado no tipo de relatório
        if (this.tipoRelatorio === 'faturamentoDiario') {
          this.dadosGrid = null;
        } else {
          this.dadosGrid = [];
        }
        this.loading = false;
      });
  }

  public filtroAPI() {
    if (this.dataInicial && this.dataFinal && !this.validarDataFinal()) {
      console.warn('Datas inválidas selecionadas. A data final deve estar entre a data inicial e o último dia do mês da data inicial.');
      return; 
    }

    // Definir as datas aplicadas quando o usuário clica em Filtrar
    this.dataInicialAplicada = new Date(this.dataInicial);
    this.dataFinalAplicada = new Date(this.dataFinal);

    // Limpar dados baseado no tipo de relatório
    if (this.tipoRelatorio === 'faturamentoDiario') {
      this.dadosGrid = null;
    } else {
      this.dadosGrid = [];
    }
    this.loading = true;

    this.f.data_inicial.setValue(moment(this.dataInicial).format('DD-MM-YYYY'));
    this.f.data_final.setValue(moment(this.dataFinal).format('DD-MM-YYYY'));

    this.filtrosSelecionados();
    this.getAll();

    this.displayFiltros = false;
  }

  private filtrosSelecionados() {
    const dealers = [];
    this.incluirFiltroDealers(dealers);
    this.filtros.dealers = dealers.map(r => r.valor);

    if (this.filtros.dealers.length != 0) {
      this.f.dealers.setValue(this.filtros.dealers[0]);
    } else {
      this.f.dealers.setValue('');
    }
  }

  public getListagemComFiltros(): boolean {
    return this.dataInicial
      || this.dataFinal
      || this.f.regiao.value
  }

  public selecionarDealer() {
    this.displayFiltrosDealers = true;
  }

  public removerDealer(dealer: any) {
    dealer.selecionado = false;
    const index = this.filtros.dealers;
    if (index > -1) {
      this.filtros.dealers.splice(index, dealer);
      this.filtrosSelecionados();
    }
  }

  public onAdicionarDealerFiltro(dealer: any) {
    dealer.selecionado = true;
    const sel = [];
    this.incluirFiltroDealers(sel);
    this.filtros.dealers = sel;
  }

  public exibirFiltro() {
    this.displayFiltros = true;
  }

  private incluirFiltroDealers(filtros: any[]) {
    if (this.filtrosDealers.selecionados().length > 0) {
      const item = { 'chave': 'dealers', 'valor': [] };
      this.filtrosDealers.selecionados().forEach(element => {
        item.valor.push(element.id);
      });
      filtros.push(item);
    }
  }

  public limparFiltros() {
    this.f.regiao.setValue('');
  }

  public limpaDataInicial() {
    const dataInicio = new Date();
    this.dataInicial = new Date(dataInicio.getUTCFullYear(), dataInicio.getMonth(), 1);
    this.onDataInicialChange();
  }

  public limpaDataFinal() {
    const dataFim = new Date();
    this.dataFinal = new Date(dataFim.getFullYear(), dataFim.getMonth(), dataFim.getDate());
  }

  public onDataInicialChange() {
    if (this.dataInicial) {
      this.minDateFinal = new Date(this.dataInicial);
      
      const ultimoDiaMes = new Date(this.dataInicial.getFullYear(), this.dataInicial.getMonth() + 1, 0);
      this.maxDateFinal = ultimoDiaMes;
      
      const hoje = new Date();
      const mesAtual = hoje.getMonth();
      const anoAtual = hoje.getFullYear();
      const mesDataInicial = this.dataInicial.getMonth();
      const anoDataInicial = this.dataInicial.getFullYear();
      
      const ehMesFuturo = (anoDataInicial > anoAtual) || 
                          (anoDataInicial === anoAtual && mesDataInicial > mesAtual);
      
      if (ehMesFuturo) {
        this.dataFinal = new Date(ultimoDiaMes);
        
        this.f.data_final.setValue(moment(this.dataFinal).format('DD-MM-YYYY'));
      }
      
      if (this.dataFinal && this.dataFinal < this.dataInicial) {
        this.dataFinal = new Date(this.dataInicial);
      }
      
      if (this.dataFinal && this.dataFinal > this.maxDateFinal) {
        this.dataFinal = new Date(this.maxDateFinal);
      }

      // Verificar se houve mudança na data inicial
      this.verificarMudancaData();
    }
  }

  public onDataFinalChange() {
    // Verificar se houve mudança na data final
    this.verificarMudancaData();
  }

  private verificarMudancaData() {
    // Se já temos dados carregados e as datas mudaram
    const temDados = (this.tipoRelatorio === 'faturamentoDiario' && this.dadosGrid) || 
                     (this.tipoRelatorio === 'regional' && this.dadosGrid && this.dadosGrid.length > 0);
    
    if (temDados && this.dataInicialAnterior && this.dataFinalAnterior) {
      const dataInicialMudou = !moment(this.dataInicial).isSame(this.dataInicialAnterior);
      const dataFinalMudou = !moment(this.dataFinal).isSame(this.dataFinalAnterior);
      
      if (dataInicialMudou || dataFinalMudou) {
        this.mostrarAvisoMudancaData = true;
        this.dadosDesatualizados = true;
      }
    }
  }

  public aplicarFiltroData() {
    this.mostrarAvisoMudancaData = false;
    this.dadosDesatualizados = false;
    this.dataInicialAnterior = new Date(this.dataInicial);
    this.dataFinalAnterior = new Date(this.dataFinal);
    
    // Definir as datas aplicadas quando o usuário aplica o filtro
    this.dataInicialAplicada = new Date(this.dataInicial);
    this.dataFinalAplicada = new Date(this.dataFinal);
    
    this.filtroAPI();
  }

  public ignorarAvisoData() {
    this.mostrarAvisoMudancaData = false;
    this.dadosDesatualizados = false;
  }

  public validarDataFinal(): boolean {
    if (!this.dataInicial || !this.dataFinal) {
      return true;
    }
    return this.dataFinal >= this.dataInicial && this.dataFinal <= this.maxDateFinal;
  }

  public getMaxDateFinal(): Date {
    if (this.dataInicial) {
      return new Date(this.dataInicial.getFullYear(), this.dataInicial.getMonth() + 1, 0);
    }
    return new Date();
  }

  public getMinDateFinal(): Date {
    return this.dataInicial || new Date();
  }

  public exportExcel() {
    this.loading = true;

    this.service.getDados(
      this.f.data_inicial.value,
      this.f.data_final.value,
      this.f.dealers.value,
      this.f.regiao.value,
      this.tipoRelatorio
    ).subscribe(dados => {
      
      // Debug: verificar dados recebidos
      console.log('Dados recebidos do serviço:', dados);
      
      // Para faturamento diário, extrair os dados do objeto retornado
      let dadosParaExportar = dados;
      let datasAPI = null;
      if (this.tipoRelatorio === 'faturamentoDiario' && dados.dados) {
        dadosParaExportar = dados.dados;
        datasAPI = {
          dataInicial: dados.dataInicial,
          dataFinal: dados.dataFinal
        };
        console.log('Dados extraídos para exportação:', dadosParaExportar);
        console.log('Datas da API:', datasAPI);
      }
      
      const nomeModuloCompleto = this.tipoRelatorio === 'regional' 
        ? `relatorio-regional`
        : `relatorio-faturamento-diario`;
      
      if (dadosParaExportar.length === 0) {
        console.warn('Nenhum registro para exportar!');
        this.loading = false;
        return;
      }
      
      try {
        this.exportExcelService.nomeModulo = nomeModuloCompleto;
        
        // Transformar dados antes de exportar
        const dadosTransformados = this.transformarDadosParaExportacao(dadosParaExportar, datasAPI);
        const dadosOrdenados = this.ordenarColunasParaExportacao(dadosTransformados);
        
        // Debug: verificar a ordem das colunas
        if (dadosOrdenados.length > 0) {
          console.log('Ordem das colunas no Excel:', Object.keys(dadosOrdenados[0]));
        }
        
        this.exportExcelService.ExportTOExcelJSON(dadosOrdenados);
      } catch (error) {
        console.error('Erro ao executar export Excel:', error);
      }
      
      this.loading = false;
    }, error => {
      console.error('Erro ao exportar dados:', error);
      this.loading = false;
    });
  }

  private transformarDadosParaExportacao(dados: any[], datasAPI?: any): any[] {
    if (this.tipoRelatorio === 'faturamentoDiario') {
      return this.transformarDadosFaturamentoDiario(dados, datasAPI);
    } else {
      return this.transformarDadosRegional(dados);
    }
  }

  private transformarDadosFaturamentoDiario(dados: any[], datasAPI?: any): any[] {
    const dadosTransformados: any[] = [];
    
    // Obter as datas do período da API
    let dataInicialAPI: moment.Moment;
    let dataFinalAPI: moment.Moment;
    
    // Usar as datas da API se disponíveis, senão usar as datas do formulário
    if (datasAPI && datasAPI.dataInicial && datasAPI.dataFinal) {
      dataInicialAPI = moment(datasAPI.dataInicial);
      dataFinalAPI = moment(datasAPI.dataFinal);
      console.log('Usando datas da API:', dataInicialAPI.format('DD/MM/YYYY'), 'a', dataFinalAPI.format('DD/MM/YYYY'));
    } else {
      // Fallback para as datas do formulário
      dataInicialAPI = this.f.data_inicial.value ? moment(this.f.data_inicial.value) : moment();
      dataFinalAPI = this.f.data_final.value ? moment(this.f.data_final.value) : moment();
      console.log('Usando datas do formulário:', dataInicialAPI.format('DD/MM/YYYY'), 'a', dataFinalAPI.format('DD/MM/YYYY'));
    }
    
    console.log('Período da API:', dataInicialAPI.format('DD/MM/YYYY'), 'a', dataFinalAPI.format('DD/MM/YYYY'));
    
    // Gerar array de datas do período
    const datasDoPeriodo: string[] = [];
    const dataAtual = moment(dataInicialAPI);
    
    while (dataAtual.isSameOrBefore(dataFinalAPI)) {
      datasDoPeriodo.push(dataAtual.format('DD/MM/YYYY'));
      dataAtual.add(1, 'day');
    }
    
    console.log('Datas do período geradas:', datasDoPeriodo);
    
    dados.forEach(dealer => {
      const linhaBase: any = {
        'Região': dealer.regiao || '',
        'Dealer': dealer.name || '',
        'Total': dealer.total || 0,
        'Cancelados / Devolvidos': dealer.totalCancelDevol ?? 0,
        'Varejo': dealer.totalVarejo ?? 0
      };

      // Adicionar colunas dos dias se existirem
      if (dealer.dias) {
        console.log('Dias do dealer:', dealer.dias);
        console.log('Chaves dos dias:', Object.keys(dealer.dias));
        
        // Mapear os dias para as datas corretas do período
        Object.keys(dealer.dias).forEach((diaKey, index) => {
          // Usar o índice para mapear diretamente com as datas do período
          if (index < datasDoPeriodo.length) {
            const dataFormatada = datasDoPeriodo[index];
            linhaBase[dataFormatada] = dealer.dias[diaKey] || 0;
            console.log(`Mapeado ${diaKey} -> ${dataFormatada}: ${dealer.dias[diaKey]}`);
          }
        });
      }

      // Garantir que todos os campos estejam presentes
      if (!linhaBase['Região']) linhaBase['Região'] = '';
      if (!linhaBase['Dealer']) linhaBase['Dealer'] = '';
      if (!linhaBase['Total']) linhaBase['Total'] = 0;

      dadosTransformados.push(linhaBase);
    });

    // Debug: verificar se os dados foram transformados corretamente
    if (dadosTransformados.length > 0) {
      console.log('Dados transformados - primeiro item:', dadosTransformados[0]);
      console.log('Chaves do primeiro item:', Object.keys(dadosTransformados[0]));
      console.log('Número de chaves:', Object.keys(dadosTransformados[0]).length);
    }

    return dadosTransformados;
  }

  private transformarDadosRegional(dados: any[]): any[] {
    const dadosTransformados: any[] = [];
    
    dados.forEach(dealer => {
      // No regional, Cancelados/Devolvidos, Varejo e Venda direta vêm da família TOTAL (não da raiz do dealer)
      const familiaTotal = dealer.familias?.find((f: any) => f.familia === 'TOTAL') || {};
      
      const linhaBase: any = {
        'Região': dealer.regiao || '',
        'Dealer': dealer.name || '',
        'Last Month STK': dealer.lastMonthStk || 0,
        'Showroom Traffic': dealer.showroomTraffic || 0,
        'Leads Marca': dealer.leadsMarca || 0,
        'CR %': dealer.crPercentual || 0,
        'Cancelados / Devolvidos': familiaTotal.cancelamentoDevolucao ?? 0,
        'Varejo': familiaTotal.varejo ?? 0,
        'Venda direta': familiaTotal.vendaDireta ?? 0
      };

      // Adicionar dados das famílias se existirem
      if (dealer.familias && Array.isArray(dealer.familias)) {
        dealer.familias.forEach((familia: any) => {
          const familiaNome = familia.familia || '';
          
          // Adicionar campos da família apenas se existirem
          if (familia.leads !== undefined) linhaBase[`${familiaNome} - Leads`] = familia.leads;
          if (familia.tgt !== undefined) linhaBase[`${familiaNome} - TGT`] = familia.tgt;
          if (familia.stkDaysCover !== undefined) linhaBase[`${familiaNome} - STK Days Cover`] = familia.stkDaysCover;
          if (familia.inventory !== undefined) linhaBase[`${familiaNome} - Inventory`] = familia.inventory;
          if (familia.orderGAC !== undefined) linhaBase[`${familiaNome} - Pedido`] = familia.orderGAC;
          if (familia.sales !== undefined) linhaBase[`${familiaNome} - Proposta`] = familia.sales;
          if (familia.retailInvoice !== undefined) linhaBase[`${familiaNome} - Faturamento`] = familia.retailInvoice;
          if (familiaNome === 'TOTAL') {
            linhaBase[`${familiaNome} - Cliente visitou loja`] =
              familia.visitouShowroom ?? dealer.visitouShowroom ?? 0;
            linhaBase[`${familiaNome} - Test-drive`] =
              familia.testDrive ?? dealer.testDrive ?? 0;
          }
        });
      }

      dadosTransformados.push(linhaBase);
    });

    return dadosTransformados;
  }

  private changeLanguageValue() {
    this.conversaoLibraConverte.preencher();
    this.subscription = this.translate.onLangChange
      .subscribe((event: LangChangeEvent) => {
        this.loading = true;
        this.atualizarTextoRegioes();
        this.getAll();
      });
  }

  private atualizarTextoRegioes() {
    if (this.regioes.length > 0) {
      this.regioes[0].nome = this.translate.instant('relatorioVeiculos.veiculoRegional.filtros.todosRegionais');
    }
  }

  public voltar() {
    window.history.back();
  }

  get regiao(): any[] {
    return this.regioes;
  }

  private ordenarColunasParaExportacao(dados: any[]): any[] {
    if (dados.length === 0) return dados;

    // Obter todas as chaves únicas de todos os objetos
    const todasAsChaves = new Set<string>();
    dados.forEach(item => {
      Object.keys(item).forEach(key => todasAsChaves.add(key));
    });

    if (this.tipoRelatorio === 'faturamentoDiario') {
      return this.ordenarColunasFaturamentoDiario(dados, Array.from(todasAsChaves));
    } else {
      return this.ordenarColunasRegional(dados, Array.from(todasAsChaves));
    }
  }

  private ordenarColunasFaturamentoDiario(dados: any[], todasAsChaves: string[]): any[] {
    // Para faturamento diário: Região, Dealer, Dias (ordenados por data), Total
    const ordemColunas = ['Região', 'Dealer'];

    // Separar e ordenar colunas de datas (formato DD/MM/YYYY)
    const colunasDatas = todasAsChaves.filter(key => {
      const regexData = /^\d{2}\/\d{2}\/\d{4}$/;
      return regexData.test(key);
    });

    // Ordenar datas cronologicamente
    colunasDatas.sort((a, b) => {
      const dataA = moment(a, 'DD/MM/YYYY');
      const dataB = moment(b, 'DD/MM/YYYY');
      return dataA.isBefore(dataB) ? -1 : dataA.isAfter(dataB) ? 1 : 0;
    });

    // Adicionar datas ordenadas
    ordemColunas.push(...colunasDatas);

    // Adicionar Total, Cancelados / Devolvidos e Varejo no final
    ordemColunas.push('Total');
    ordemColunas.push('Cancelados / Devolvidos');
    ordemColunas.push('Varejo');

    // Reordenar os dados
    return dados.map(item => {
      const itemOrdenado: any = {};
      ordemColunas.forEach(coluna => {
        if (item.hasOwnProperty(coluna)) {
          itemOrdenado[coluna] = item[coluna];
        }
      });
      return itemOrdenado;
    });
  }

  private ordenarColunasRegional(dados: any[], todasAsChaves: string[]): any[] {
    // Para relatório regional: Região, Dealer, campos fixos, famílias, Cancelados/Varejo/Venda direta, totais
    const colunasFixas = ['Região', 'Dealer'];
    const colunasCamposFixos = ['Last Month STK', 'Showroom Traffic', 'Leads Marca', 'CR %'];
    const colunasCanceladosVarejoVendaDireta = ['Cancelados / Devolvidos', 'Varejo', 'Venda direta'];
    const colunasFinais = ['Total'];

    // Separar colunas de famílias (que contêm " - ")
    const colunasFamilias = todasAsChaves.filter(key => 
      key.includes(' - ') && !colunasFixas.includes(key) && !colunasCamposFixos.includes(key) 
        && !colunasCanceladosVarejoVendaDireta.includes(key) && !colunasFinais.includes(key)
    );

    // Ordenar famílias alfabeticamente
    colunasFamilias.sort();

    // Montar ordem final: Fixas, Campos Fixos, Famílias (ordenadas), Cancelados/Varejo/Venda direta, Finais
    const ordemColunas = [...colunasFixas, ...colunasCamposFixos, ...colunasFamilias, ...colunasCanceladosVarejoVendaDireta, ...colunasFinais];

    // Reordenar os dados
    return dados.map(item => {
      const itemOrdenado: any = {};
      ordemColunas.forEach(coluna => {
        if (item.hasOwnProperty(coluna)) {
          itemOrdenado[coluna] = item[coluna];
        }
      });
      return itemOrdenado;
    });
  }
}